import {
  dispatchDeleteRequest,
  dispatchGetRequest,
  dispatchPostRequest,
  dispatchPutWithBodyRequest,
} from '../util/apiUtils';
import { getShopperId } from '../util/auth';
import SmsSubscription from '../types/sms/SmsSubscription';
import {
  trackSubscriptionChangeFailure,
  trackSubscriptionFetchFailure,
  trackSubscriptionUnsubscribeFailure,
} from './newRelic/smsHandlingNewRelicTrackingApi';

const prodOmniBaseUrl = process.env.PRODUCTION_OMNI_BASE_URL || '';
const stagingOmniBaseUrl = process.env.STAGING_OMNI_BASE_URL || '';
const smsApiPrefix = '/api/v0/sms-subscriptions';

/**
 * Gets the base url for the OMNI service
 * @param {string} environment The environment to send OMNI service requests too.
 */
const getOMNIBaseUrl = (environment: string | null): string => {
  if (!environment) {
    console.error(
      `Unable to derive service config for a null/empty environment, using STAGING environment as sensible fallback.`
    );
    return stagingOmniBaseUrl;
  }
  switch (environment) {
    case 'PRODUCTION':
      return prodOmniBaseUrl;
    case 'STAGING':
      return stagingOmniBaseUrl;
    default:
      console.warn(
        `Unsupported environment type of ${environment}, using STAGING environment as sensible fallback.`
      );
      return stagingOmniBaseUrl;
  }
};

export const getSubscription = async (
  environment: string | null,
  orderNumber: string | null,
  shopperId: string
): Promise<SmsSubscription | null> => {
  if (!orderNumber) {
    return null;
  }

  const metricsAdditionalData = {
    orderId: orderNumber,
  };

  const baseUrl = getOMNIBaseUrl(environment);
  const url = `${baseUrl}${smsApiPrefix}/orders/${orderNumber}`;
  try {
    const response = await dispatchGetRequest({ url });
    const subscription = (await response.json()) as SmsSubscription;
    return subscription;
  } catch (err) {
    trackSubscriptionFetchFailure(shopperId, metricsAdditionalData);
    throw err;
  }
};

const subscribe = async (
  environment: string | null,
  subscription: SmsSubscription
) => {
  const metricsAdditionalData = {
    orderId: subscription.orderNumber,
  };
  const shopperId = getShopperId();
  const baseUrl = getOMNIBaseUrl(environment);

  if (!subscription.orderNumber) {
    throw new Error('Order number is required to subscribe to SMS.');
  }

  const url = `${baseUrl}${smsApiPrefix}/orders/${subscription.orderNumber}`;

  try {
    await dispatchPostRequest({ url, body: subscription });
  } catch (err) {
    trackSubscriptionChangeFailure(shopperId, metricsAdditionalData);
    throw err;
  }
};

const update = async (
  environment: string | null,
  subscription: SmsSubscription
) => {
  const metricsAdditionalData = {
    orderId: subscription.orderNumber,
  };
  const shopperId = getShopperId();
  const baseUrl = getOMNIBaseUrl(environment);

  if (!subscription.orderNumber) {
    throw new Error('Order number is required to subscribe to SMS.');
  }

  const url = `${baseUrl}${smsApiPrefix}/orders/${subscription.orderNumber}`;

  try {
    await dispatchPutWithBodyRequest({ url, body: subscription });
  } catch (err) {
    trackSubscriptionChangeFailure(shopperId, metricsAdditionalData);
    throw err;
  }
};

export const saveSubscription = async (
  environment: string | null,
  subscription: SmsSubscription,
  isNewSubscription: boolean
) => {
  if (isNewSubscription) {
    await subscribe(environment, subscription);
  } else {
    await update(environment, subscription);
  }
};

export const unsubscribe = async (
  environment: string | null,
  orderNumber: string | null
) => {
  const metricsAdditionalData = {
    orderId: orderNumber,
  };
  const shopperId = getShopperId();
  const baseUrl = getOMNIBaseUrl(environment);

  const url = `${baseUrl}${smsApiPrefix}/orders/${orderNumber}`;

  try {
    const response = await dispatchDeleteRequest({ url });
    const { phoneNumberUnsubscribed } = await response.json();
    return phoneNumberUnsubscribed;
  } catch (err) {
    trackSubscriptionUnsubscribeFailure(shopperId, metricsAdditionalData);
    throw err;
  }
};
